import { useMemo, useState } from "react";
import { ConferenceLink } from "./ConferenceLink";
import { TeamLink } from "./TeamLink";
import { TeamLogo } from "./TeamLogo";
import type {
  ConferencesResponse,
  FootballStandingsResponse,
  RecordsStanding,
  TeamsResponse,
} from "./types";
import { useFetch } from "./useFetch";

// Football subdivision options for the inline filter. The backend
// accepts "", "fbs", "fcs" for the subdivision query parameter; we
// store the empty case as the null-selected option.
const SUBDIVISIONS: { key: string; label: string }[] = [
  { key: "", label: "All" },
  { key: "fbs", label: "FBS" },
  { key: "fcs", label: "FCS" },
];

const SPORT_LABEL: Record<string, string> = {
  football: "Football",
};

// buildNameLookup resolves the catalog's team display name for each
// slug. Scoreboard emits hyphen slugs; catalog emits underscore slugs;
// standings persists whatever ingest wrote. Match both to avoid
// rendering raw slugs in the Team column.
function buildNameLookup(teams: TeamsResponse | null): Map<string, string> {
  const m = new Map<string, string>();
  if (!teams) return m;
  for (const t of teams.teams) {
    m.set(t.slug, t.shortName);
    m.set(t.slug.replace(/-/g, "_"), t.shortName);
    m.set(t.slug.replace(/_/g, "-"), t.shortName);
  }
  return m;
}

function titleCase(slug: string): string {
  return slug
    .split(/[-_]/)
    .map((p) => (p.length > 0 ? p[0].toUpperCase() + p.slice(1) : p))
    .join(" ");
}

export type FootballStandingsPageProps = {
  sport: string;
  division: string;
  conference: string;
  onConferenceChange: (c: string) => void;
};

export function FootballStandingsPage({
  sport,
  division,
  conference,
  onConferenceChange,
}: FootballStandingsPageProps) {
  // Subdivision is page-local — not URL-backed yet. If sharing a
  // subdivision-filtered view becomes important, promote this into
  // useURLState alongside conference and mode.
  const [subdivision, setSubdivision] = useState<string>("");

  const standingsURL = subdivision
    ? `/api/v1/standings/football/${encodeURIComponent(division)}?subdivision=${encodeURIComponent(subdivision)}`
    : `/api/v1/standings/football/${encodeURIComponent(division)}`;
  const standings = useFetch<FootballStandingsResponse>(standingsURL);
  const teams = useFetch<TeamsResponse>(
    `/api/v1/teams?sport=${encodeURIComponent(sport)}&division=${encodeURIComponent(division)}`,
  );
  const conferences = useFetch<ConferencesResponse>("/api/v1/conferences");

  const nameFor = useMemo(() => {
    const names = buildNameLookup(teams.kind === "ok" ? teams.data : null);
    return (slug: string) => names.get(slug) ?? titleCase(slug);
  }, [teams]);

  const conferenceLabelFor = useMemo(() => {
    const labels = new Map<string, string>();
    if (conferences.kind === "ok") {
      for (const c of conferences.data.conferences) {
        labels.set(c.slug, c.shortName || c.name);
      }
    }
    return (slug: string | undefined) =>
      slug ? (labels.get(slug) ?? slug) : "";
  }, [conferences]);

  const filtered: RecordsStanding[] = useMemo(() => {
    if (standings.kind !== "ok") return [];
    if (!conference) return standings.data.standings;
    return standings.data.standings.filter(
      (s) => s.conference === conference,
    );
  }, [standings, conference]);

  const activeConferenceLabel = conferenceLabelFor(conference);

  return (
    <section className="rpi-page">
      <header className="rpi-header">
        <h2>
          {division.toUpperCase()} {SPORT_LABEL[sport] ?? sport} Standings
        </h2>
        <div className="rpi-controls">
          <label className="header-select">
            <span className="visually-hidden">Subdivision</span>
            <select
              value={subdivision}
              onChange={(e) => setSubdivision(e.target.value)}
              aria-label="Subdivision"
            >
              {SUBDIVISIONS.map((s) => (
                <option key={s.key || "all"} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          {conference && (
            <button type="button" onClick={() => onConferenceChange("")}>
              Clear conference filter
            </button>
          )}
        </div>
      </header>

      {standings.kind === "loading" && <p>Loading…</p>}
      {standings.kind === "error" && (
        <p role="alert">Failed to load standings: {standings.message}</p>
      )}
      {standings.kind === "ok" && filtered.length === 0 && (
        <p>
          {standings.data.seasonUnavailable
            ? `${SPORT_LABEL[sport] ?? sport} ${division.toUpperCase()} season hasn't started yet — standings will populate once games are played.`
            : `No ${SPORT_LABEL[sport] ?? sport} ${division.toUpperCase()}${subdivision ? ` ${subdivision.toUpperCase()}` : ""}${conference ? ` ${activeConferenceLabel}` : ""} teams yet.`}
        </p>
      )}
      {standings.kind === "ok" && filtered.length > 0 && (
        <table className="standings">
          <thead>
            <tr>
              <th scope="col" className="num">
                Rank
              </th>
              <th scope="col">Team</th>
              <th scope="col">Conf</th>
              <th scope="col" className="num">
                W-L
              </th>
              <th scope="col" className="num" title="Win percentage">
                Win%
              </th>
              <th scope="col" className="num" title="Points scored">
                PF
              </th>
              <th scope="col" className="num" title="Points allowed">
                PA
              </th>
              <th scope="col" className="num" title="Point differential">
                Diff
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => (
              <tr key={s.teamSlug}>
                <td className="num">{s.rank}</td>
                <td>
                  <TeamLink
                    slug={s.teamSlug}
                    sport={sport}
                    division={division}
                  >
                    <TeamLogo slug={s.teamSlug} alt="" />
                    <span className="team-name">{nameFor(s.teamSlug)}</span>
                  </TeamLink>
                </td>
                <td>
                  <ConferenceLink
                    conference={s.conference ?? ""}
                    sport={sport}
                    division={division}
                    label={conferenceLabelFor(s.conference)}
                  />
                </td>
                <td className="num">
                  {s.wins}-{s.losses}
                </td>
                <td className="num">{s.winPercentage.toFixed(3)}</td>
                <td className="num">{s.pointsFor}</td>
                <td className="num">{s.pointsAgainst}</td>
                <td className="num">
                  {s.pointDiff > 0 ? `+${s.pointDiff}` : s.pointDiff}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
